import { useContext, useEffect, useState } from "react"
import { SidePopupLayout } from "src/component/SidePopupLayout"
import ApiContext from "src/provider/API/call-service"
import VisibilityContext from "src/provider/state-manager/visibilityProvider"
import UserContext from "src/provider/state-manager/userProvider"
import { AbsoluteContainer, AppSpan, AppText, Button, CustomContainer, FlexRow, Form, FormGroup, FormGroupWithIcon, GridContainer, ScreenTitle } from "src/style"
import { FiChevronDown } from "react-icons/fi"
import { TxPinVerificationD3, InvestmentConfirmationD3, AppCenterPopupD3, CenterPopupHeaderD3 } from "src/component"
import utility, { moneyFormat, handleAmountChange } from "src/utils/utility"
import helpers from "src/utils/helpers"
import AppInfoContext from "src/provider/state-manager/appInfoProvider"

export const NewInvestmentD3: React.FC<any> = ({getUpdatedInvestments, close}) => {
    const {API} = useContext(ApiContext)
    const {notifier} = useContext(VisibilityContext)
    const {user: {accountWithBalanceDetails}} = useContext(UserContext)
    const [amount, setAmount] = useState("")
    const [input, setInput] = useState({
        amount: '', liquidationAccount: '', startDate: '', pin: '',
        tenure: '', investmentResult: false, accountName: '', productCode: ''
    })
    const [products, setProducts] = useState<any>(null)
    const [selectedProduct, setSelectedProduct] = useState<any>(null)
    const [availableTenures, setAvailableTenures] = useState<any>([])
    const [isLoading, setIsLoading] = useState(false)
    const [view, setView] = useState('new-investment')
    const [fixedDepositInfo, setFixedDepositInfo] = useState({acruedInterest: 0, expectedReturn: 0, maturationDate: ''})
    const {info:institutionData} = useContext(AppInfoContext)

    useEffect(() => {
        if (accountWithBalanceDetails?.length === 1) {
            setInput({...input, liquidationAccount: accountWithBalanceDetails[0].nuban || accountWithBalanceDetails[0].number})
        }
    }, [accountWithBalanceDetails])

    useEffect(() => {
        handleFetchProducts()
    }, [])

    async function handleFetchProducts () {
        setIsLoading(true)
        let response = await API.getProducts()
        setIsLoading(false)
        if (response) {
            setProducts(response)
        }
    }

    const handleInput = (e: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>) => {
        if (e.target.name === 'amount') {
            input.investmentResult = false
            handleAmountChange(e.target.value,"input",setAmount,setInput,input,e.target.name)
        }
        else setInput({...input, [e.target.name]: e.target.value, investmentResult: false})
    }

    function handleSelectedProduct(item: any) {
        const productObj = JSON.parse(item)
        setSelectedProduct(productObj)
        input.tenure = ''
        setInput({
            ...input,
            productCode: productObj.productCode,
            investmentResult: false,
        })
        const tenures = helpers.generateAvailableTenures(productObj.tenure)
        setAvailableTenures(tenures)
    }

    function handleProceed(e: React.FormEvent<HTMLButtonElement | HTMLFormElement>) {
        e.preventDefault()
        const accountName = helpers.getAccountDetailsByParam(accountWithBalanceDetails, 'nuban', input.liquidationAccount)?.name
        setInput({...input, accountName})

        if (!Number(input.amount)) return notifier.show('Invalid fixed deposit amount')

        if (!input.investmentResult) {
            const {productCode, amount, startDate} = input

            setIsLoading(true)
            const fixedDepositData = helpers.computeFixedDepositData(Number(input.amount), Number(input.tenure), Number(selectedProduct.interestRate),  new Date(input.startDate))
            setIsLoading(false)

            if (fixedDepositData) {
                setFixedDepositInfo(fixedDepositData)
                setInput({...input, investmentResult: true})
                return false
            }
        }
        setView('investment-confirmation')
    }

    async function handleSubmit() {
        const response = await API.initiateFixedDeposit(input)
        if (response) {
            getUpdatedInvestments()
            close()
        }
    }

    return (
        <>
            { view === 'new-investment' &&
                <AppCenterPopupD3 height={input.investmentResult ? '62' : '53'} zIndex='6' overflow='hidden' close={close}>
                    <CenterPopupHeaderD3 title='Add New Investment' close={close} />
                    { (isLoading) &&
                        <AbsoluteContainer top='0' left='0' zIndex='3' width='100' sizeUnit='%' height="full">
                            <GridContainer height='100' hUnit="%" bgColor='rgba(255, 255, 255, 0.5)' radius="2">
                                <AppSpan textSize='4' color='#0D968F' className='fa fa-spinner fa-spin' />
                            </GridContainer>
                        </AbsoluteContainer>
                    }
                    <CustomContainer 
                        height={input.investmentResult ? '62' : '53'}
                        style={{maxHeight: '88%'}}
                        className="popup-content-container"
                    >
                        <Form onSubmit={handleProceed}>
                            <FormGroup>
                                <label>Amount</label>
                                <input 
                                    placeholder='Enter Amount' 
                                    name='amount'
                                    value={amount}
                                    onChange={handleInput}
                                    autoComplete="off"
                                    required
                                />
                            </FormGroup>
                            <FormGroupWithIcon>
                                <label>Investment Name</label>
                                <div className="input-with-icon">
                                    <select
                                        name='tenure'
                                        value={selectedProduct ? JSON.stringify(selectedProduct) : ''}
                                        style={{ color: input.tenure ? '#000000' : '#667085' }}
                                        onChange={(e) => handleSelectedProduct(e.target.value)}
                                        required
                                    >
                                        <option value="" disabled>Select Investment</option>
                                        {
                                            products?.map((item: any, index: number) => {
                                                return (
                                                    <option key={index} value={JSON.stringify(item)}>{item.productName}</option>
                                                )
                                            })
                                        }
                                    </select>
                                    <FiChevronDown className="input-with-icon-tag" />
                                </div>
                            </FormGroupWithIcon>
                            <FormGroupWithIcon>
                                <label>Tenure</label>
                                <div className="input-with-icon">
                                    <select
                                        name='tenure'
                                        value={input.tenure || ''}
                                        style={{ color: input.tenure ? '#000000' : '#667085' }}
                                        onChange={handleInput}
                                        required
                                    >
                                        <option value="" disabled>Select Tenure</option>
                                        {availableTenures.map((item: any, index: number) => (
                                            <option key={index} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                    <FiChevronDown className="input-with-icon-tag" />
                                </div>
                            </FormGroupWithIcon>
                            <FormGroup>
                                <label>Start Date</label>
                                <input 
                                    placeholder='dd/mm/yyyy' 
                                    type='date'
                                    name='startDate'
                                    min={helpers.getInputValidDate()}
                                    style={{color: input.startDate ? '#000000' : '#667085'}}
                                    value={input.startDate}
                                    onChange={handleInput}
                                    autoComplete="off"
                                    required
                                />
                            </FormGroup>
                            { input.investmentResult &&
                                <FlexRow 
                                    gap="2" padding="1.5" minHeight="7.7" 
                                    radius="0.8" bgColor="#E7F6F5" borderColor="#E7F6F5"
                                    justifyContent="space-between" bottomMargin="2"
                                >
                                    <GridContainer width="auto" sizeUnit="" alignItems="start">
                                        <AppSpan color="#3A3A3A">Expected Return</AppSpan>
                                        <AppText fontWeight="700" color="#0D968F">{utility.moneyFormat(fixedDepositInfo?.expectedReturn || 0, true,institutionData?.defaultCurrency)}</AppText>
                                    </GridContainer>
                                    <GridContainer width="auto" sizeUnit="" alignItems="center">
                                        <AppSpan color="#3A3A3A">Maturity Date</AppSpan>
                                        <AppText fontWeight="700" color="#0D968F">{helpers.splitDateTime(fixedDepositInfo?.maturationDate || '0000-01-01T00:00:00').slashedDate}</AppText>
                                    </GridContainer>
                                    <GridContainer width="auto" sizeUnit="" alignItems="end">
                                        <AppSpan color="#3A3A3A">Maturity Date</AppSpan>
                                        <AppText fontWeight="700" color="#0D968F">{selectedProduct.interestRate}%</AppText>
                                    </GridContainer>
                                </FlexRow>
                            }
                            <FormGroupWithIcon>
                                <label>Source Account</label>
                                <div className="input-with-icon">
                                    <select 
                                        name='liquidationAccount'
                                        value={input.liquidationAccount}
                                        style={{color: input.liquidationAccount ? '#000000' : '#667085'}}
                                        onChange={handleInput}
                                        disabled={accountWithBalanceDetails?.length === 1}
                                        required
                                    >
                                        <option value="">Select Account</option>
                                        {
                                            accountWithBalanceDetails?.map((item: any, index: number) => {
                                                return (
                                                    <option 
                                                        value={item.nuban || item.number} 
                                                        key={index}
                                                    >
                                                        {item.nuban || item.number} - ({moneyFormat(item?.balance?.withdrawableAmount,institutionData?.defaultCurrency)})
                                                    </option>
                                                )
                                            })
                                        }
                                    </select>
                                    <FiChevronDown className="input-with-icon-tag" />
                                </div>
                            </FormGroupWithIcon>
                            <FlexRow topMargin='2.5'>
                                <Button 
                                    width='100' sizeUnit="%" height='4' 
                                    borderColor='#0D968F' hoverBgColor='#fff' 
                                    hoverColor='#0D968F' titleSize='1.4'
                                >
                                    Submit
                                </Button>
                            </FlexRow>
                        </Form>
                    </CustomContainer>
                </AppCenterPopupD3>
            }
            { view === 'pin' &&
                <TxPinVerificationD3
                    close={close}
                    onPinChange={(pin: string) => setInput({...input, pin})}
                    next={handleSubmit} 
                />
            }
            { view === 'investment-confirmation' &&
                <InvestmentConfirmationD3
                    close={close}
                    data={{...input, ...fixedDepositInfo}}
                    type={'investment'}
                    next={() => setView('pin')} 
                    back={() => setView('new-investment')}
                />
            }
        </>
    )
}